/* eslint-disable react/jsx-boolean-value */
/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import useValidateMedia from 'components/hooks/useValidateMedia';
import useHalfSpace from 'components/hooks/useHalfSpace';
import Ad, { ProductRanges, Statuses as AdStatuses } from 'models/business/Ad';
import Asset, { Categories as AssetCategories } from 'models/business/Asset';
import ActionButton from 'components/Common/ActionButton';
import SectionLabel from 'components/pages/ResourceSetting/Widgets/SectionLabel';
import GoogleDisplayReference from 'components/pages/ResourceSetting/Widgets/GoogleDisplayReference';
// import PhonePreview from 'components/pages/ResourceSetting/Widgets/PhonePreview';
import ProductRangeField from 'components/pages/ResourceSetting/Widgets/ProductRangeField';
import HostingUrlField from 'components/pages/ResourceSetting/Widgets/HostingUrlField';
import ContentFeedField from 'components/pages/ResourceSetting/Widgets/ContentFeedField';
import ShortTitleField from 'components/pages/ResourceSetting/Widgets/ShortTitleField';
import GoogleDescriptionField from 'components/pages/ResourceSetting/Widgets/GoogleDescriptionField';
import SingleImageUploadField from 'components/pages/ResourceSetting/Widgets/SingleImageUploadField';
import YoutubeUrlField from 'components/pages/ResourceSetting/Widgets/YoutubeUrlField';
import HideableContainer from 'components/Common/HideableContainer';
import API from 'api';
import Styles from './index.module.css';

const EmojiRegex = new RegExp(/[\u{1f300}-\u{1f5ff}\u{1f900}-\u{1f9ff}\u{1f600}-\u{1f64f}\u{1f680}-\u{1f6ff}\u{2600}-\u{26ff}\u{2700}-\u{27bf}\u{1f1e6}-\u{1f1ff}\u{1f191}-\u{1f251}\u{1f004}\u{1f0cf}\u{1f170}-\u{1f171}\u{1f17e}-\u{1f17f}\u{1f18e}\u{3030}\u{2b50}\u{2b55}\u{2934}-\u{2935}\u{2b05}-\u{2b07}\u{2b1b}-\u{2b1c}\u{3297}\u{3299}\u{303d}\u{00a9}\u{00ae}\u{2122}\u{23f3}\u{24c2}\u{23e9}-\u{23ef}\u{25b6}\u{23f8}-\u{23fa}]/ug);

export default function MainForm({
  ad,
  onChangeAd,
  // onContinue,
  // eslint-disable-next-line no-unused-vars
  firstEdit,
}) {
  const [productRange, setProductRange] = useState(ProductRanges.Total);
  const [firstLoad, setFirstLoad] = useState(true);
  const [isAdSaved, setIsAdSaved] = useState(true);
  const { validateImage } = useValidateMedia();
  const { restrictByHalfSpace } = useHalfSpace();
  const history = useHistory();
  const [original, setOriginal] = useState({
    ad: new Ad(),
    media: {},
    local: {},
  });
  const [media, setMedia] = useState({
    prepopulated: null,
    local: null,
  });
  const [contentFeed, setContentFeed] = useState({
    prepopulated: null,
    local: null,
  });

  const hasUnsavedChanges = useMemo(() => (
    !original.ad.equals(ad) || original.media !== media || original.contentFeed !== contentFeed
  ), [ad, original, media, contentFeed]);

  const deleteAllMedia = async (categories = [AssetCategories.Media, AssetCategories.ContentFeed]) => {
    const deletionPromises = [];
    ad.assets.forEach((asset) => {
      if (categories.includes(asset.category)) {
        deletionPromises.push(API.ad.deleteAdMedia(asset.id));
      }
    });

    return Promise.allSettled(deletionPromises);
  };

  const uploadFile = async (file, category) => API.ad.postAdMedia(ad.id, category, file);

  const filterEgregious = (s) => {
    const ss = s.replace(EmojiRegex, '');
    let newString = '';
    let hasExclamation = false;
    for (let i = 0; i < ss.length; i += 1) {
      const c = ss[i];
      if (c === '!' || c === '！') {
        // eslint-disable-next-line no-continue
        if (hasExclamation) continue;
        hasExclamation = true;
      }
      newString += c;
    }
    return newString;
  };

  const handleChangeField = (e, field, length) => {
    const newAd = ad.clone();
    const value = length ? restrictByHalfSpace(filterEgregious(e.target.value), length, newAd[field]) : e.target.value;
    newAd[field] = value;
    onChangeAd(newAd);
  };

  const saveAd = async () => {
    if (!hasUnsavedChanges) return true;
    setIsAdSaved(false);
    let newOriginalMedia = original.media;
    let newOriginalContent = original.contentFeed;
    const clearAdAssets = (categories = []) => {
      if (categories.includes(AssetCategories.Media)) {
        const newMedia = {
          prepopulated: null,
          local: null,
        };
        setMedia(newMedia);
        newOriginalMedia = newMedia;
      }
      if (categories.includes(AssetCategories.ContentFeed)) {
        const newContentFeed = {
          prepopulated: null,
          local: null,
        };
        setContentFeed(newContentFeed);
        newOriginalContent = newContentFeed;
      }
      const newAd = ad.clone();
      newAd.assets = [...newAd.assets].filter((a) => !categories.includes(a));
      onChangeAd(newAd);
      return newAd;
    };

    const deletionCategories = [];
    if (!media.prepopulated) deletionCategories.push(AssetCategories.Media);
    if (!contentFeed.prepopulated || productRange === ProductRanges.Total) deletionCategories.push(AssetCategories.ContentFeed);
    await deleteAllMedia(deletionCategories);

    const localDeletionCategories = [];
    if (!media.local && !media.prepopulated) localDeletionCategories.push(AssetCategories.Media);
    if ((!contentFeed.local && !contentFeed.prepopulated) || ad.contentFeedCategory === AssetCategories.MarketContentFeed) localDeletionCategories.push(AssetCategories.ContentFeed);
    const newAd = clearAdAssets(localDeletionCategories);

    const newAssets = [];

    if (media.local) {
      const result = await uploadFile(media.local, AssetCategories.Media);
      if (!result.error) {
        const newMedia = {
          prepopulated: new Asset(result.resultData),
          local: null,
        };
        setMedia(newMedia);
        newOriginalMedia = newMedia;
        newAssets.push(new Asset(result.resultData));
      }
    } else if (media.prepopulated) {
      newAssets.push(new Asset(media.prepopulated));
    }

    if (contentFeed.local) {
      const result = await uploadFile(contentFeed.local, AssetCategories.ContentFeed);
      if (!result.error) {
        const newContentFeed = {
          prepopulated: new Asset(result.resultData),
          local: null,
        };
        setContentFeed(newContentFeed);
        newOriginalContent = newContentFeed;
        newAssets.push(new Asset(result.resultData));
      }
    } else if (contentFeed.prepopulated && ad.contentFeedCategory !== AssetCategories.MarketContentFeed) {
      newAssets.push(new Asset(contentFeed.prepopulated));
    }

    // const newAd = ad.clone();
    newAd.status = AdStatuses.Incomplete;
    newAd.injectAssets(newAssets);
    onChangeAd(newAd);

    const result = await API.ad.postUpdateAd(newAd.toJSON());
    if (result.error) {
      alert(result.error.errorMsg);
      setIsAdSaved(true);
      return null;
    }
    setOriginal({
      ad: newAd,
      media: newOriginalMedia,
      contentFeed: newOriginalContent,
    });
    setIsAdSaved(true);
    return true;
  };

  const submitAd = async () => {
    saveAd().then((ok) => ok && history.push('/MyAds'));
  };

  useEffect(() => {
    if (!firstLoad || !ad.id) return;
    setFirstLoad(false);
    const originalAd = ad;
    let originalMedia = media;
    let originalContent = contentFeed;
    const mediaAsset = ad.assets.find((a) => a.category === AssetCategories.Media);
    if (mediaAsset) {
      const newMedia = {
        prepopulated: mediaAsset,
        local: null,
      };
      setMedia(newMedia);
      originalMedia = newMedia;
    }
    const contentAsset = ad.assets.find((a) => a.category === AssetCategories.ContentFeed);
    if (contentAsset) {
      const newContentFeed = {
        prepopulated: contentAsset,
        local: null,
      };
      setContentFeed(newContentFeed);
      originalContent = newContentFeed;
      setProductRange(ProductRanges.Selected);
    }
    setOriginal({
      ad: originalAd,
      media: originalMedia,
      contentFeed: originalContent,
    });
  }, [ad]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      style={{ position: 'relative' }}
    >
      <Box marginBottom={5}>
        <Box marginBottom={2}>
          <SectionLabel>各種版位一次滿足全方位銷售產品</SectionLabel>
        </Box>
        <GoogleDisplayReference />
      </Box>
      <SectionLabel>SSC 素材設定</SectionLabel>
      {/* <div className={Styles.phonePreviewContainer}>
        <PhonePreview className={Styles.phonePreview} />
      </div> */}
      <Box marginBottom={3}>
        <ShortTitleField value={ad.shortTitle} onChange={(e) => handleChangeField(e, 'shortTitle', 25)} />
      </Box>
      <Box marginBottom={3}>
        <GoogleDescriptionField value={ad.googleDescription} onChange={(e) => handleChangeField(e, 'googleDescription', 90)} />
      </Box>
      <Box marginBottom={3}>
        <SingleImageUploadField
          value={(media.local ?? media.prepopulated ?? {}).name}
          onChange={async (file) => {
            if (file) {
              const valid = await validateImage(file, 1200, 628);
              if (!valid) return;
            }
            setMedia({
              prepopulated: null,
              local: file,
            });
          }}
        />
      </Box>
      <Box marginBottom={3}>
        <YoutubeUrlField value={ad.youtubeUrl} onChange={(e) => handleChangeField(e, 'youtubeUrl')} />
      </Box>
      <Box marginBottom={3}>
        <HostingUrlField required={true} value={ad.hostingUrl} onChange={(e) => handleChangeField(e, 'hostingUrl')} />
      </Box>
      <Box marginBottom={3}>
        <ProductRangeField
          value={productRange}
          onChange={(range) => {
            let newContentFeedCategory = AssetCategories.ContentFeed;
            if (range === ProductRanges.Total) {
              newContentFeedCategory = AssetCategories.MarketContentFeed;
            }
            setProductRange(range);

            const newAd = ad.clone();
            newAd.contentFeedCategory = newContentFeedCategory;
            onChangeAd(newAd);
          }}
          contentFeed={contentFeed}
          setContentFeed={() => setContentFeed({
            prepopulated: null,
            local: null,
          })}
        />
      </Box>
      <HideableContainer hidden={productRange !== ProductRanges.Selected}>
        <Box marginBottom={3}>
          <ContentFeedField
            value={(contentFeed.local ?? contentFeed.prepopulated ?? {}).name}
            onChange={(file) => {
              setContentFeed({
                prepopulated: null,
                local: file,
              });
            }}
          />
        </Box>
      </HideableContainer>
      <div className={Styles.formButtons}>
        <ActionButton style={{ color: '#FFFFFF' }} onClick={saveAd} disabled={!hasUnsavedChanges}>
          儲存進度
        </ActionButton>
        <ActionButton style={{ color: '#FFFFFF' }} onClick={() => history.push('/MyAds')}>
          {firstEdit ? '返回我的廣告' : '取消'}
          {/* 取消 */}
        </ActionButton>
        <ActionButton
          style={{ color: '#FFFFFF' }}
          disabled={(
            !isAdSaved
            || !ad.valid
            || !(media.prepopulated || media.local)
            || (ad.contentFeedCategory === AssetCategories.ContentFeed && !(contentFeed.local || contentFeed.prepopulated))
          )}
          onClick={submitAd}
        >
          完成
        </ActionButton>
      </div>
    </Box>
  );
}

MainForm.propTypes = {
  ad: PropTypes.instanceOf(Ad),
  onChangeAd: PropTypes.func,
  // onContinue: PropTypes.func,
  firstEdit: PropTypes.bool,
};

MainForm.defaultProps = {
  ad: new Ad(),
  onChangeAd: () => {},
  // onContinue: () => {},
  firstEdit: false,
};
